import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { useContextoDeLista } from "@/features/assistente/contexto-de-tela";
import { useState } from "react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Tag, Plus, Trash2, Edit2, Users } from "lucide-react";

const PRESET_COLORS = [
  "#25D366", "#128C7E", "#075E54",
  "#3B82F6", "#6366F1", "#8B5CF6",
  "#F59E0B", "#EF4444", "#EC4899",
  "#10B981", "#06B6D4", "#F97316",
  "#64748B", "#84CC16", "#E11D48",
];

type TagForm = {
  name: string;
  color: string;
  description: string;
};

const emptyForm: TagForm = { name: "", color: "#25D366", description: "" };

export default function Tags() {
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<TagForm>(emptyForm);
  const utils = trpc.useUtils();

  const { data: tags, isLoading } = trpc.tags.list.useQuery();

  useContextoDeLista({
    titulo: "Tags de contato",
    itens: (tags ?? []).map((t: any) => ({ id: t.id, nome: t.name, contatos: t.contactCount ?? null })),
    amostra: 40,
  });


  const createMut = trpc.tags.create.useMutation({
    onSuccess: () => {
      utils.tags.list.invalidate();
      toast.success("Tag criada!");
      setShowForm(false);
      setForm(emptyForm);
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const updateMut = trpc.tags.update.useMutation({
    onSuccess: () => {
      utils.tags.list.invalidate();
      toast.success("Tag atualizada!");
      setShowForm(false);
      setEditId(null);
      setForm(emptyForm);
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const deleteMut = trpc.tags.delete.useMutation({
    onSuccess: () => { utils.tags.list.invalidate(); toast.success("Tag removida!"); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const handleSave = () => {
    if (!form.name.trim()) { toast.error("Nome é obrigatório"); return; }
    if (editId) {
      updateMut.mutate({ id: editId, name: form.name, color: form.color, description: form.description || undefined });
    } else {
      createMut.mutate({ name: form.name, color: form.color, description: form.description || undefined });
    }
  };

  const handleEdit = (tag: any) => {
    setEditId(tag.id);
    setForm({ name: tag.name, color: tag.color ?? "#25D366", description: tag.description ?? "" });
    setShowForm(true);
  };

  return (
    <PermissionGuard permission="contacts:manage_tags">
    <DashboardLayout>
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Tags</h1>
            <p className="text-muted-foreground text-sm mt-0.5">
              Organize seus contatos com tags coloridas para segmentar campanhas
            </p>
          </div>
          <Button size="sm" onClick={() => { setEditId(null); setForm(emptyForm); setShowForm(true); }}>
            <Plus className="h-4 w-4 mr-1.5" /> Nova Tag
          </Button>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-muted animate-pulse rounded-xl" />)}
          </div>
        ) : tags?.length === 0 ? (
          <div className="text-center py-16">
            <Tag className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground font-medium">Nenhuma tag criada</p>
            <p className="text-sm text-muted-foreground mt-1">
              Crie tags para segmentar seus contatos em campanhas
            </p>
            <Button size="sm" className="mt-4" onClick={() => { setForm(emptyForm); setShowForm(true); }}>
              <Plus className="h-4 w-4 mr-1.5" /> Criar primeira tag
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {tags?.map((tag) => (
              <Card key={tag.id} className="hover:shadow-md transition-all group">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
                      style={{ backgroundColor: (tag.color ?? "#25D366") + "20", border: `2px solid ${(tag.color ?? "#25D366")}40` }}
                    >
                      <Tag className="h-5 w-5" style={{ color: tag.color ?? "#25D366" }} />
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleEdit(tag)}>
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive"
                        onClick={() => { if (confirm(`Remover tag "${tag.name}"?`)) deleteMut.mutate({ id: tag.id }); }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <p className="font-semibold text-sm truncate">{tag.name}</p>
                  {tag.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{tag.description}</p>
                  )}
                  <div className="flex items-center gap-1 mt-2">
                    <div className="h-2 w-2 rounded-full" style={{ backgroundColor: tag.color ?? "#25D366" }} />
                    <span className="text-xs text-muted-foreground font-mono">{tag.color ?? "#25D366"}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Dica de uso */}
        {(tags?.length ?? 0) > 0 && (
          <div className="flex items-start gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg text-sm">
            <Users className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <p className="text-muted-foreground">
              <strong className="text-foreground">Como usar:</strong> Atribua tags aos contatos na página de Contatos.
              Ao criar uma campanha, selecione as tags para segmentar o público-alvo do disparo.
            </p>
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); setEditId(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editId ? "Editar Tag" : "Nova Tag"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nome da Tag *</Label>
              <Input
                placeholder="Ex: Contadores-MT, Interesse-IA"
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Cor</Label>
              <div className="mt-2 space-y-2">
                <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      className={`h-8 w-full rounded-lg transition-all ${form.color === color ? "ring-2 ring-offset-2 ring-foreground scale-110" : "hover:scale-105"}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setForm(f => ({ ...f, color }))}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg shrink-0" style={{ backgroundColor: form.color }} />
                  <Input
                    value={form.color}
                    onChange={(e) => setForm(f => ({ ...f, color: e.target.value }))}
                    placeholder="#25D366"
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label>Descrição (opcional)</Label>
              <Textarea
                placeholder="Descreva o uso desta tag..."
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                className="mt-1 min-h-[70px]"
              />
            </div>

            {/* Preview */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground mb-2">Preview:</p>
              <span
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-full font-medium"
                style={{ backgroundColor: form.color + "20", color: form.color, border: `1px solid ${form.color}40` }}
              >
                <Tag className="h-3 w-3" />
                {form.name || "Nome da tag"}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditId(null); setForm(emptyForm); }}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={createMut.isPending || updateMut.isPending}>
              {createMut.isPending || updateMut.isPending ? "Salvando..." : editId ? "Atualizar" : "Criar Tag"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
    </PermissionGuard>
  );
}
